<script setup>
/**
 * Tola AI settings.
 *
 * Intercom-style section cards on the settings kit: a usage-and-credits card
 * fed by the admin usage endpoint, an assistants card that lists the account's
 * assistants and deep-links into the Tola AI product area for management, and
 * the feature-toggle stack. This page stays a settings surface - building or
 * tuning an assistant happens in the product area, which every link here
 * points at.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useConfig } from 'dashboard/composables/useConfig';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import CaptainUsage from 'dashboard/api/captain/usage';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BillingMeter from '../billing/components/BillingMeter.vue';
import FeatureToggle from './components/FeatureToggle.vue';
import CaptainPaywall from 'next/captain/pageComponents/Paywall.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { captainEnabled, responseLimits, fetchLimits } = useCaptain();
const { isEnterprise } = useConfig();
const { isOnChatwootCloud } = useAccount();

const captainConfigStore = useCaptainConfigStore();
const { uiFlags } = storeToRefs(captainConfigStore);

const isLoading = computed(() => uiFlags.value.isFetching);

// === Usage & credits ===

const usage = ref(null);
const isLoadingUsage = ref(true);

const fetchUsage = async () => {
  try {
    const { data } = await CaptainUsage.getUsage();
    usage.value = data;
  } catch (error) {
    usage.value = null;
  } finally {
    isLoadingUsage.value = false;
  }
};

const formatNumber = value =>
  Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const usageTiles = computed(() => {
  const totals = usage.value?.totals ?? {};
  return [
    {
      key: 'credits',
      label: t('CAPTAIN_SETTINGS.USAGE.CREDITS_USED'),
      value: formatNumber(totals.credits_used),
    },
    {
      key: 'events',
      label: t('CAPTAIN_SETTINGS.USAGE.EVENTS'),
      value: formatNumber(totals.events),
    },
  ];
});

// === Assistants ===

const assistants = useMapGetter('captainAssistants/getRecords');
const assistantUiFlags = useMapGetter('captainAssistants/getUIFlags');
const isLoadingAssistants = computed(() => assistantUiFlags.value.fetchingList);

const accountScopedRoute = (name, params = {}) => ({
  name,
  params: { accountId: route.params.accountId, ...params },
});

// The product-area index redirects to the last active assistant, or to the
// create flow when the account has none - one target covers both states.
const manageAssistantsRoute = accountScopedRoute('captain_assistants_index', {
  navigationPath: 'captain_assistants_overview_index',
});

const assistantSettingsRoute = assistant =>
  accountScopedRoute('captain_assistants_settings_index', {
    assistantId: assistant.id,
  });

// === Features (unchanged behavior) ===

const featureToggles = computed(() => [
  { key: 'label_suggestion' },
  { key: 'help_center_search', enterprise: true },
  { key: 'audio_transcription', enterprise: true },
]);

const shouldShowFeature = feature => {
  // Cloud will always see these features as long as captain is enabled
  if (isOnChatwootCloud.value && captainEnabled.value) {
    return true;
  }

  if (feature.enterprise) {
    // if the app is in enterprise mode, then we can show the feature
    // this is not the installation plan, but when the enterprise folder is missing
    return isEnterprise;
  }

  return true;
};

const isFeatureAccessible = feature => {
  // Cloud will always see these features as long as captain is enabled
  if (isOnChatwootCloud.value && captainEnabled.value) {
    return true;
  }

  if (feature.enterprise) {
    // accessible once the account's plan includes Tola AI; plans reconcile
    // the captain feature flag, so the flag is the source of truth
    return isEnterprise && captainEnabled.value;
  }

  return true;
};

async function handleFeatureToggle({ feature, enabled }) {
  try {
    await captainConfigStore.updatePreferences({
      captain_features: { [feature]: enabled },
    });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
}

async function handleModelChange({ feature, model }) {
  try {
    await captainConfigStore.updatePreferences({
      captain_models: { [feature]: model },
    });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
}

onMounted(() => {
  captainConfigStore.fetch();
  if (captainEnabled.value) {
    fetchUsage();
    fetchLimits();
    store.dispatch('captainAssistants/get');
  }
});
</script>

<template>
  <SettingsPageShell
    :title="t('CAPTAIN_SETTINGS.TITLE')"
    :description="t('CAPTAIN_SETTINGS.DESCRIPTION')"
  >
    <CaptainPaywall v-if="!captainEnabled" />

    <div
      v-else-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="t('CAPTAIN_SETTINGS.LOADING')"
      class="flex flex-col gap-5"
    >
      <Skeleton
        v-for="row in 4"
        :key="row"
        variant="text"
        :lines="2"
        :announce="false"
      />
    </div>

    <template v-else>
      <SettingsSection
        :title="t('CAPTAIN_SETTINGS.USAGE.TITLE')"
        :description="t('CAPTAIN_SETTINGS.USAGE.DESCRIPTION')"
      >
        <div class="grid grid-cols-2 divide-x divide-n-weak">
          <div
            v-for="tile in usageTiles"
            :key="tile.key"
            class="flex flex-col gap-1 px-4 py-4"
          >
            <span
              class="text-xs font-medium tracking-wider uppercase text-n-slate-10"
            >
              {{ tile.label }}
            </span>
            <Skeleton
              v-if="isLoadingUsage"
              variant="text"
              :announce="false"
              class="w-16 h-6"
            />
            <span v-else class="text-xl font-semibold text-n-slate-12">
              {{ tile.value }}
            </span>
          </div>
        </div>

        <div v-if="responseLimits" class="px-4 py-4">
          <BillingMeter
            :title="t('CAPTAIN_SETTINGS.USAGE.RESPONSES_METER')"
            v-bind="responseLimits"
          />
        </div>

        <div class="px-4 py-3">
          <RouterLink
            :to="accountScopedRoute('billing_settings_index')"
            class="inline-flex items-center gap-1 text-sm text-n-blue-11 hover:underline"
          >
            {{ t('CAPTAIN_SETTINGS.USAGE.MANAGE_BILLING') }}
            <Icon icon="i-lucide-arrow-right" class="size-3.5" />
          </RouterLink>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="t('CAPTAIN_SETTINGS.ASSISTANTS.TITLE')"
        :description="t('CAPTAIN_SETTINGS.ASSISTANTS.DESCRIPTION')"
      >
        <div
          v-if="isLoadingAssistants"
          role="status"
          aria-busy="true"
          :aria-label="t('CAPTAIN_SETTINGS.ASSISTANTS.LOADING')"
          class="flex items-center gap-3 px-4 py-4"
        >
          <Skeleton variant="circle" :announce="false" class="size-8" />
          <Skeleton
            variant="text"
            :lines="2"
            :announce="false"
            class="flex-1"
          />
        </div>

        <template v-else-if="assistants.length">
          <RouterLink
            v-for="assistant in assistants"
            :key="assistant.id"
            :to="assistantSettingsRoute(assistant)"
            class="flex items-center gap-3 px-4 py-4 group"
          >
            <span
              aria-hidden="true"
              class="flex items-center justify-center rounded-lg size-8 shrink-0 bg-n-violet-3 text-n-violet-11"
            >
              <Icon icon="i-ph-sparkle" class="size-4" />
            </span>
            <span class="flex flex-col min-w-0 flex-1 gap-0.5">
              <span
                class="text-sm font-medium truncate text-n-slate-12 group-hover:underline"
              >
                {{ assistant.name }}
              </span>
              <span
                v-if="assistant.description"
                class="text-sm truncate text-n-slate-11"
              >
                {{ assistant.description }}
              </span>
            </span>
            <Icon
              icon="i-lucide-chevron-right"
              class="size-4 shrink-0 text-n-slate-10"
            />
          </RouterLink>
        </template>

        <p v-else class="px-4 py-4 text-sm text-n-slate-11">
          {{ t('CAPTAIN_SETTINGS.ASSISTANTS.EMPTY') }}
        </p>

        <div class="px-4 py-3">
          <RouterLink
            :to="manageAssistantsRoute"
            class="inline-flex items-center gap-1 text-sm text-n-blue-11 hover:underline"
          >
            {{ t('CAPTAIN_SETTINGS.ASSISTANTS.MANAGE') }}
            <Icon icon="i-lucide-arrow-right" class="size-3.5" />
          </RouterLink>
        </div>
      </SettingsSection>

      <SettingsSection
        flush
        :title="t('CAPTAIN_SETTINGS.FEATURES.TITLE')"
        :description="t('CAPTAIN_SETTINGS.FEATURES.DESCRIPTION')"
      >
        <div class="grid gap-4">
          <FeatureToggle
            v-for="feature in featureToggles"
            v-show="shouldShowFeature(feature)"
            :key="feature.key"
            :is-allowed="isFeatureAccessible(feature)"
            :feature-key="feature.key"
            @change="handleFeatureToggle"
            @model-change="handleModelChange"
          />
        </div>
      </SettingsSection>
    </template>
  </SettingsPageShell>
</template>
